import { Component, OnInit, OnDestroy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { WorkOrderService } from '../../../core/services/work-order.service';
import { CustomerService } from '../../../core/services/customer.service';
import { FieldService } from '../../../core/services/field.service';
import { TeamService } from '../../../core/services/team.service';
import { OrganizationContextService } from '../../../core/services/organization-context.service';
import { ToastService } from '../../../core/services/toast.service';
import { WorkOrderRealtimeService } from '../../../core/services/work-order-realtime.service';
import {
  UpdateWorkOrderDto,
  WorkOrder,
  workOrderAcresDisplay,
  workOrderLocationDisplay,
  workOrderStatusBadgeClass,
  workOrderStatusLabel,
  workOrderSourceLabel,
} from '../../../core/models/work-order.model';
import { MembershipRole, RoleHelper } from '../../../core/models/team.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

/**
 * Work Order detail screen for org Owners / Chief Supervisors.
 *
 * Layout (top → bottom):
 *  1. Header card — status pill, title, customer/field, scheduled
 *     date/time, source badge.
 *  2. Assignment card — current assignee + reassign picker.
 *  3. Timeline card — created/started/completed/cancelled timestamps.
 *  4. Linked spray-record card (when `sprayRecordId` is set).
 *  5. Cancellation card (only shown when `status === 'cancelled'`).
 *  6. Action bar — Edit + Cancel.
 *
 * Edit puts the header into inline-edit mode and lets the user change
 * title / description / scheduledDate / scheduledTime / customer /
 * field. Cancel opens an in-page modal that requires a reason before
 * PATCH /work-orders/:id/cancel fires.
 *
 * Realtime — the screen watches the workOrderService's signal-backed
 * cache, which `WorkOrderRealtimeService` keeps in sync with the
 * three `work-order.*` events. A WS-driven update silently re-renders
 * the screen without an explicit reload.
 */
@Component({
  selector: 'app-work-order-details',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './work-order-details.component.html',
  styleUrls: ['./work-order-details.component.scss'],
})
export class WorkOrderDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private workOrderService = inject(WorkOrderService);
  private customerService = inject(CustomerService);
  private fieldService = inject(FieldService);
  private teamService = inject(TeamService);
  private organizationContext = inject(OrganizationContextService);
  private toast = inject(ToastService);
  // Injected just to ensure the realtime service is constructed and
  // subscribed. Not actually read here — the data flow uses the
  // signal cache directly.
  private _realtime = inject(WorkOrderRealtimeService);

  private destroy$ = new Subject<void>();

  workOrderId = signal<string | null>(null);
  loading = signal<boolean>(true);
  notFound = signal<boolean>(false);

  // Header edit state
  editing = signal<boolean>(false);
  editTitle = signal<string>('');
  editDescription = signal<string>('');
  editCustomerId = signal<string>('');
  editFieldId = signal<string>('');
  editScheduledDate = signal<string>('');
  editScheduledTime = signal<string>('');
  savingHeader = signal<boolean>(false);

  // Assignment state
  assignDraft = signal<string>('');
  savingAssign = signal<boolean>(false);

  // Cancellation modal state
  cancelModalOpen = signal<boolean>(false);
  cancelReason = signal<string>('');
  savingCancel = signal<boolean>(false);

  // The live work order, pulled from the service's signal so WS
  // refreshes flow through automatically.
  readonly workOrder = computed<WorkOrder | undefined>(() => {
    const id = this.workOrderId();
    if (!id) return undefined;
    return this.workOrderService.workOrders().find((w) => w.id === id);
  });

  readonly canEdit = computed(() => {
    const wo = this.workOrder();
    if (!wo) return false;
    return wo.status !== 'completed' && wo.status !== 'cancelled';
  });

  readonly canCancel = computed(() => {
    const wo = this.workOrder();
    return !!wo && wo.status !== 'completed' && wo.status !== 'cancelled';
  });

  readonly customers = computed(() =>
    this.customerService.customers().map((c) => ({ id: c.id, name: c.name })),
  );

  readonly fields = computed(() => {
    const customerId = this.editCustomerId();
    const all = this.fieldService.fields();
    return (customerId ? all.filter((f) => f.customerId === customerId) : all).map(
      (f) => ({ id: f.id, name: f.name, customerId: f.customerId ?? null }),
    );
  });

  readonly assignablePilots = computed(() => {
    return this.teamService
      .members()
      .filter((m) => !m.deletedAt)
      .filter((m) => {
        const role = m.combinedRole ?? m.role ?? 0;
        return (
          RoleHelper.hasRole(role, MembershipRole.Pilot) ||
          RoleHelper.hasRole(role, MembershipRole.PilotInCharge) ||
          RoleHelper.hasRole(role, MembershipRole.Owner)
        );
      })
      .map((m) => ({
        id: m.profileId,
        name: this.memberDisplayName(m.firstName, m.lastName, m.email),
      }));
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      this.workOrderId.set(id);
      if (id) {
        void this.load(id);
      }
    });

    // Prime team list — assignment + edit need it.
    const orgId = this.organizationContext.getCurrentOrganizationId();
    if (orgId) {
      void this.teamService.loadMembers(orgId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async load(id: string): Promise<void> {
    this.loading.set(true);
    this.notFound.set(false);
    // Try cache first for snappy paint, then refresh from API.
    const cached = this.workOrderService.getById(id);
    if (!cached) {
      const wo = await this.workOrderService.fetchOne(id);
      if (!wo) this.notFound.set(true);
    } else {
      // Background refresh — silent, fire & forget.
      void this.workOrderService.fetchOne(id);
    }
    this.loading.set(false);
    const wo = this.workOrder();
    if (wo) {
      this.assignDraft.set(wo.assignedPilotProfileId ?? '');
    }
  }

  // ─── Header edit ──────────────────────────────────────────────────

  startEdit(): void {
    const wo = this.workOrder();
    if (!wo) return;
    this.editTitle.set(wo.title);
    this.editDescription.set(wo.description ?? '');
    this.editCustomerId.set(wo.customerId ?? '');
    this.editFieldId.set(wo.fieldId ?? '');
    this.editScheduledDate.set(this.toDateInput(wo.scheduledDate));
    this.editScheduledTime.set(wo.scheduledTime ?? '');
    this.editing.set(true);
  }

  cancelEdit(): void {
    this.editing.set(false);
  }

  async saveHeader(): Promise<void> {
    const wo = this.workOrder();
    if (!wo) return;
    const title = this.editTitle().trim();
    if (!title) {
      this.toast.error('Title is required.');
      return;
    }
    this.savingHeader.set(true);
    try {
      const dto: UpdateWorkOrderDto = {
        title,
        description: this.editDescription().trim() || undefined,
        customerId: this.editCustomerId() || null,
        fieldId: this.editFieldId() || null,
        scheduledDate: this.editScheduledDate() || null,
        scheduledTime: this.editScheduledTime().trim() || null,
      };
      const updated = await this.workOrderService.update(wo.id, dto);
      if (updated) {
        this.toast.success('Work order updated');
        this.editing.set(false);
      } else {
        this.toast.error(
          this.workOrderService.error() ?? 'Failed to update work order',
        );
      }
    } finally {
      this.savingHeader.set(false);
    }
  }

  /** Clear the selected field when the customer changes mid-edit. */
  onEditCustomerChange(customerId: string): void {
    this.editCustomerId.set(customerId);
    const fid = this.editFieldId();
    if (!fid) return;
    const stillValid = this.fields().some((f) => f.id === fid);
    if (!stillValid) this.editFieldId.set('');
  }

  // ─── Assignment ──────────────────────────────────────────────────

  async saveAssignment(): Promise<void> {
    const wo = this.workOrder();
    if (!wo) return;
    const next = this.assignDraft() || null;
    if ((wo.assignedPilotProfileId ?? null) === next) return;
    this.savingAssign.set(true);
    try {
      const updated = await this.workOrderService.update(wo.id, {
        assignedPilotProfileId: next,
      });
      if (updated) {
        this.toast.success(
          next ? 'Assignment updated' : 'Work order unassigned',
        );
      } else {
        this.toast.error(
          this.workOrderService.error() ?? 'Failed to update assignment',
        );
        // Reset draft to current state on failure.
        this.assignDraft.set(wo.assignedPilotProfileId ?? '');
      }
    } finally {
      this.savingAssign.set(false);
    }
  }

  // ─── Cancellation ─────────────────────────────────────────────────

  openCancelModal(): void {
    this.cancelReason.set('');
    this.cancelModalOpen.set(true);
  }

  closeCancelModal(): void {
    if (this.savingCancel()) return;
    this.cancelModalOpen.set(false);
  }

  async confirmCancel(): Promise<void> {
    const wo = this.workOrder();
    if (!wo) return;
    const reason = this.cancelReason().trim();
    if (!reason) {
      this.toast.error('Please provide a cancellation reason.');
      return;
    }
    this.savingCancel.set(true);
    try {
      const updated = await this.workOrderService.cancel(wo.id, { reason });
      if (updated) {
        this.toast.success('Work order cancelled');
        this.cancelModalOpen.set(false);
      } else {
        this.toast.error(
          this.workOrderService.error() ?? 'Failed to cancel work order',
        );
      }
    } finally {
      this.savingCancel.set(false);
    }
  }

  back(): void {
    this.router.navigate(['/work-orders']);
  }

  // ─── View helpers ─────────────────────────────────────────────────

  statusLabel = workOrderStatusLabel;
  statusBadgeClass = workOrderStatusBadgeClass;
  sourceLabel = workOrderSourceLabel;
  locationDisplay = workOrderLocationDisplay;
  acresDisplay = workOrderAcresDisplay;

  /**
   * Resolve the assigned-pilot display name. Trusts the Hub-denormalized
   * `assignedPilotName` first, then falls back to team-service lookup,
   * then to the explicit `fallback` (typically the WO's own value).
   * Order matters — the team service is org-scoped and may not have
   * loaded yet, so the WO-baked name wins.
   */
  pilotName(
    profileId: string | null | undefined,
    fallback?: string | null,
  ): string {
    if (!profileId) return 'Unassigned';
    if (fallback) return fallback;
    const member = this.teamService.members().find((m) => m.profileId === profileId);
    if (member) {
      return this.memberDisplayName(member.firstName, member.lastName, member.email);
    }
    return 'Assigned';
  }

  customerName(customerId: string | null | undefined, fallback?: string | null): string {
    if (fallback) return fallback;
    if (!customerId) return '—';
    const c = this.customerService.customers().find((c) => c.id === customerId);
    return c?.name || '—';
  }

  fieldName(fieldId: string | null | undefined, fallback?: string | null): string {
    if (fallback) return fallback;
    if (!fieldId) return '—';
    const f = this.fieldService.fields().find((f) => f.id === fieldId);
    return f?.name || '—';
  }

  /** Pretty-format the actor name for timeline entries. Falls back
   *  to "User" when we can't resolve the profile id. */
  actorName(profileId: string | null | undefined): string {
    if (!profileId) return 'System';
    const member = this.teamService.members().find((m) => m.profileId === profileId);
    if (!member) return 'User';
    return this.memberDisplayName(member.firstName, member.lastName, member.email);
  }

  private memberDisplayName(
    firstName?: string,
    lastName?: string,
    email?: string,
  ): string {
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    return name || email || 'Unknown';
  }

  /** Convert an ISO timestamp / date-only string to the `yyyy-MM-dd`
   *  format the `<input type="date">` expects. */
  private toDateInput(value: string | null | undefined): string {
    if (!value) return '';
    // Already date-only? Pass through.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
